import { Link } from 'react-router-dom';
import { ParentShell } from '@/features/parent/ParentShell';
import { usePrivacySettings, useSavePrivacySettings } from '@/features/privacy/use-privacy';
import { useAcceptPolicy, useCurrentPolicies } from '@/features/policies/use-policies';
import { SegmentedChoice, Toggle } from '@/components/ui/Toggle';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeading } from '@/components/ui/Card';
import { ErrorNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';

/**
 * Privacy, and the terms this account is bound by. [Parent steps 3, 11, 27]
 *
 * These settings were the last third of "complete your profile, preferences,
 * notification and privacy settings" — the column existed, nothing read it.
 * Notification preferences deliberately stay beside the inbox they silence;
 * what lives here is the set of choices about who sees what.
 */

const POLICY_LABEL: Record<string, string> = {
  TERMS_OF_SERVICE: 'Terms of service',
  PRIVACY_POLICY: 'Privacy policy',
  SAFETY_GUIDELINES: 'Safety guidelines',
  PAYMENT_TERMS: 'Payment terms',
};

function PrivacyPanel() {
  const settings = usePrivacySettings();
  const save = useSavePrivacySettings();
  const current = settings.data;

  return (
    <Card>
      <CardHeading>Who sees what</CardHeading>

      {settings.isPending && (
        <SkeletonGroup label="Loading your privacy settings" className="mt-4 space-y-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </SkeletonGroup>
      )}

      {current && (
        <div className="mt-2 divide-y divide-line">
          <SegmentedChoice
            label="How much of your teen's job chats you read"
            value={current.messageVisibility}
            disabled={save.isPending}
            onChange={(messageVisibility) => save.mutate({ messageVisibility })}
            options={[
              {
                value: 'FULL',
                label: 'Everything',
                description:
                  'You read the messages between your teen and an employer, as they do.',
              },
              {
                value: 'METADATA',
                label: 'Just the activity',
                description:
                  'You see who is talking, when, and how much — but not what was written. You stay in the room either way: a minor never has a private thread with an adult here, and anything flagged for safety still reaches you.',
              },
            ]}
          />

          <Toggle
            label="Let other parents find you"
            description="Parents connect by email address. With this off, your address returns nothing — the same answer as an address with no account at all."
            checked={current.discoverableByParents}
            disabled={save.isPending}
            onChange={(discoverableByParents) => save.mutate({ discoverableByParents })}
          />

          <Toggle
            label="Show my town to parents I'm connected with"
            description="Your city and state on your card in their network. Your name and photo stay visible."
            checked={current.showLocationToConnections}
            disabled={save.isPending}
            onChange={(showLocationToConnections) => save.mutate({ showLocationToConnections })}
          />
        </div>
      )}

      {save.isError && (
        <div className="mt-3">
          <ErrorNote message={presentApiError(save.error, 'Could not save that').message} />
        </div>
      )}
    </Card>
  );
}

function PoliciesPanel() {
  const policies = useCurrentPolicies();
  const accept = useAcceptPolicy();
  const items = policies.data?.items ?? [];

  return (
    <Card>
      <CardHeading
        action={
          policies.data && policies.data.outstanding > 0 ? (
            <Badge tone="sun">{policies.data.outstanding} to agree to</Badge>
          ) : undefined
        }
      >
        Terms you are bound by
      </CardHeading>

      {policies.isPending && (
        <SkeletonGroup label="Loading policies" className="mt-4 space-y-3">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </SkeletonGroup>
      )}

      {policies.data && items.length === 0 && (
        <p className="mt-4 text-sm text-slate-500">
          Nothing published yet. When TeenTack publishes terms, they appear here for you to read
          and agree to.
        </p>
      )}

      {items.length > 0 && (
        <ul className="mt-4 divide-y divide-line">
          {items.map((policy) => (
            <li key={policy.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">
                  {POLICY_LABEL[policy.policyType] ?? policy.policyType}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Version {policy.version} · in force since {policy.effectiveDate}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={policy.contentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-semibold text-brand-700 underline decoration-brand-200 underline-offset-4 hover:decoration-brand-500"
                >
                  Read it
                </a>
                {policy.acceptedAt ? (
                  <Badge tone="safe">Agreed</Badge>
                ) : (
                  <Button
                    size="sm"
                    disabled={accept.isPending}
                    onClick={() => accept.mutate(policy.id)}
                  >
                    Agree
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {accept.isError && (
        <div className="mt-3">
          <ErrorNote
            message={presentApiError(accept.error, 'Could not record that').message}
          />
        </div>
      )}
    </Card>
  );
}

export function SettingsPage() {
  return (
    <ParentShell
      title="Settings"
      description="Privacy, and the terms you have agreed to. Notification switches live next to your inbox."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <PrivacyPanel />
        <PoliciesPanel />
      </div>

      <p className="mt-6 text-sm text-slate-500">
        Looking for what you get notified about?{' '}
        <Link
          to="/notifications"
          className="font-semibold text-brand-700 underline decoration-brand-200 underline-offset-4 hover:decoration-brand-500"
        >
          That is on your notifications page
        </Link>
        .
      </p>
    </ParentShell>
  );
}
